import { EditedSession, FormPageType } from "../context/formData.reducer";
import { LinkPageType } from "../context/linkData.reducer";
import { getOriginalSessionsById, OriginalSession } from "./getOriginalSessionsById";
import { isSessionWeekOutsideThreshold } from "./isOutsideThreshold";
import { sessionsHaveTheSameResources } from "./sessionsHaveTheSameResources";

/**
 * True when the edition changed something that cannot be applied retroactively:
 * schedule, day, classroom or instructor. Intervals are deliberately compared
 * out, since adding future weeks is not by itself a reason to fork — the weeks
 * already taught keep their assignment either way.
 */
const hasResourceEdits = (session: EditedSession, original: OriginalSession): boolean =>
  !sessionsHaveTheSameResources({ ...session, intervals: [] }, { ...original, intervals: [] });

/**
 * True when the session is scheduled on both sides of the blocking threshold,
 * which is what forces the split: the blocked weeks have to keep the original
 * assignment while the editable ones take the new one.
 */
const hasMixedCheckedIntervals = (session: EditedSession, thresholdDate: string): boolean => {
  let hasCheckedPast = false;
  let hasCheckedNonPast = false;

  for (const interval of session?.intervals ?? []) {
    if (!interval?.checked) continue;

    if (isSessionWeekOutsideThreshold(thresholdDate, interval, session)) {
      hasCheckedPast = true;
    } else {
      hasCheckedNonPast = true;
    }

    if (hasCheckedPast && hasCheckedNonPast) return true;
  }

  return false;
};

// Sessions created during this edition (`isNew`) or cloned ones carry a uuid
// instead of a backend id, and have no past assignment to preserve.
const isPersistedSession = (session: EditedSession): boolean =>
  !!session?.id && !session.id.includes("-") && !session?.isNew;

/**
 * Return every edited session that has to be forked before submitting.
 *
 * The decision is taken per session, against its own original assignment,
 * walking `savedSessions` — the accumulated edition. Reading the multi-edit form
 * state (`editedSessions` / `resourceEdits`) instead would only describe the
 * session currently selected, ignoring every session edited individually before
 * it: that is what made N individual editions collapse into a single fork
 * (CHS-2818).
 *
 * Time complexity: O(n * w)
 * Where `n` is the number of saved sessions and `w` the number of weeks of the term.
 *
 * @param form FormPageType
 * @param link LinkPageType
 * @param thresholdDate string | null
 * @param canEditPastSessions boolean
 * @return EditedSession[]
 */
export const needsPastFork = (
  form: FormPageType,
  link: LinkPageType,
  thresholdDate: string | null,
  canEditPastSessions: boolean,
): EditedSession[] => {
  if (canEditPastSessions || !thresholdDate) return [];

  const originalSessionsById = getOriginalSessionsById(link?.sections ?? []);
  const sessionsToDelete = form?.sessionsToDelete ?? {};

  return (form?.savedSessions ?? []).filter(session => {
    if (!isPersistedSession(session)) return false;
    if (Object.prototype.hasOwnProperty.call(sessionsToDelete, session.id)) return false;

    const original = originalSessionsById[session.id];
    if (!original) return false;

    return hasResourceEdits(session, original) && hasMixedCheckedIntervals(session, thresholdDate);
  });
};
